import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { PersonRole } from "@ha/data/basic";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Select, Store } from "@ngxs/store";
import { BehaviorSubject, Observable } from "rxjs";

import { PermitCreateSelectors } from "../../../permit-create/states/permit-create.selectors";
import { PermitPersonWithStatus, RecentTeamMemberSort } from "../../../shared/models/permit-person.model";
import { PermitTeamActions } from "../../states/permit-team.action";
import { PermitTeamSelectors } from "../../states/permit-team.selectors";

@UntilDestroy()
@Component({
    selector: "pmt-last-used-persons-list",
    templateUrl: "last-used-persons-list.component.html",
    styleUrls: ["last-used-persons-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class LastUsedPersonsListComponent implements OnInit {
    @Input()
    public permitId: number;

    @Input()
    public role: PersonRole;

    @Output()
    public createCertificateEvent = new EventEmitter<PermitPersonWithStatus>();

    @Output()
    public addNewEvent = new EventEmitter<void>();

    @Select(PermitTeamSelectors.recentTeamMembers)
    public recentTeamMembers$: Observable<PermitPersonWithStatus[]>;

    @Select(PermitTeamSelectors.recentTeamMembersFullyLoaded)
    public recentTeamMembersFullyLoaded$: Observable<boolean>;

    public query = "";

    @Select(PermitTeamSelectors.recentTeamMembersSearchResult)
    public result$: Observable<PermitPersonWithStatus[]>;

    @Select(PermitTeamSelectors.hasColleagueMembers)
    public hasColleagueMembers$: Observable<boolean>;

    @Select(PermitTeamSelectors.hasSharedFromPremiumMembers)
    public hasSharedFromPremiumMembers$: Observable<boolean>;

    @Select(PermitCreateSelectors.isPremium)
    public isPremium$: Observable<boolean>;

    private INITIAL_TAKE = 40;
    private LOAD_MORE_TAKE = 10;
    private take = this.INITIAL_TAKE;

    public loading$ = new BehaviorSubject<boolean>(false);
    public includeColleagues = true;
    public includeSharedFromPremium = true;
    public sort = RecentTeamMemberSort.lastUsed;
    public sortByDescending = true;
    public filterPanelOpen = false;
    public sortPanelOpen = false;
    public RecentTeamMemberSort = RecentTeamMemberSort;

    constructor(private store: Store) {}

    public ngOnInit(): void {
        this.loadTeamMembers(true);
    }

    private loadTeamMembers(restart: boolean): void {
        if (restart) {
            this.take = this.INITIAL_TAKE;
        }

        this.loading$.next(true);
        this.store.dispatch(new PermitTeamActions.GetRecentTeamMembers(
            this.permitId,
            this.take,
            this.includeColleagues,
            this.includeSharedFromPremium,
            this.sort,
            this.sortByDescending,
            restart,
            this.query.toLowerCase(),
        )).pipe(untilDestroyed(this)).subscribe(() => {
            this.loading$.next(false);
        });
    }

    public loadMore(): void {
        this.take = this.LOAD_MORE_TAKE;
        this.loadTeamMembers(false);
    }

    public search(query: string) {
        this.query = query;
        this.loadTeamMembers(true);
    }

    public clearSearchResults() {
        this.query = "";
        this.search("");
    }

    public toggleFilterPanel(): void {
        if (this.filterPanelOpen) {
            this.closePanels();
            return;
        }

        this.filterPanelOpen = true;
        this.sortPanelOpen = false;
    }

    public toggleSortPanel(): void {
        this.sortPanelOpen = !this.sortPanelOpen;
        this.filterPanelOpen = false;
    }

    public closePanels(): void {
        const applyFilter = this.filterPanelOpen;

        this.filterPanelOpen = false;
        this.sortPanelOpen = false;

        if (applyFilter) {
            this.loadTeamMembers(true);
        }
    }

    public selectSort(sort: RecentTeamMemberSort): void {
        if (this.sort === sort) {
            this.sortByDescending = !this.sortByDescending;
        } else {
            this.sort = sort;
            this.sortByDescending = [
                RecentTeamMemberSort.lastUsed,
                RecentTeamMemberSort.colleagues,
                RecentTeamMemberSort.sharedFromPremium,
            ].includes(sort);
        }

        this.closePanels();
        this.loadTeamMembers(true);
    }

    public addNew(): void {
        this.addNewEvent.emit();
    }

    public createCertificate(person: PermitPersonWithStatus) {
        this.createCertificateEvent.emit(person);
    }
}
